import { useState } from 'react';
import api from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { toast } from 'sonner';
import { Heart, DollarSign, Loader2 } from 'lucide-react';

const TIP_AMOUNTS = [5, 10, 25, 50, 100];

export function TipModal({ open, onOpenChange, creator }) {
  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTip = async () => {
    const tipAmount = customAmount ? parseFloat(customAmount) : amount;
    
    if (tipAmount < 1) {
      toast.error('Minimum tip is $1.00');
      return;
    }
    if (tipAmount > 500) {
      toast.error('Maximum tip is $500.00');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/tips/create-checkout', {
        creator_id: creator.id,
        amount: tipAmount,
        message: message || null,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tip');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="tip-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Send a Tip
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Creator Info */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl">
            <Avatar className="w-12 h-12">
              <AvatarImage src={creator?.avatar} />
              <AvatarFallback>{creator?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{creator?.display_name}</p>
              <p className="text-sm text-muted-foreground">@{creator?.username}</p>
            </div>
          </div>

          {/* Amount Selection */}
          <div>
            <p className="text-sm font-medium mb-3">Select Amount</p>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {TIP_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => {
                    setAmount(amt);
                    setCustomAmount('');
                  }}
                  className={`py-2 rounded-xl font-semibold transition-colors ${
                    amount === amt && !customAmount
                      ? 'bg-primary text-white'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid={`tip-${amt}`}
                >
                  ${amt}
                </button>
              ))}
            </div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="number"
                placeholder="Custom amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="pl-10"
                min="1"
                max="500"
                step="0.01"
                data-testid="custom-amount-input"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <p className="text-sm font-medium mb-2">Add a message (optional)</p>
            <Textarea
              placeholder="Say something nice..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={200}
              data-testid="tip-message-input"
            />
            <p className="text-xs text-muted-foreground mt-1">{message.length}/200</p>
          </div>

          {/* Submit */}
          <Button
            onClick={handleTip}
            disabled={loading}
            className="w-full rounded-full h-12"
            data-testid="send-tip-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Heart className="w-4 h-4 mr-2" />
                Send ${customAmount || amount} Tip
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Payments are securely processed by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TipModal;
